import React, { useState, useEffect } from "react";
import { Table, Button, Space, Input, Row, Col, Modal, Form, Select, message, Upload, DatePicker } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, UploadOutlined, FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const ExpenseForm = () => {
  const [searchText, setSearchText] = useState("");
  const [selectedFilterExpense, setSelectedFilterExpense] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]); // Store filtered data
  const [expenseOptions, setExpenseOptions] = useState([]);

  useEffect(() => {
    fetchData();
    fetchExpenseTypes();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get("http://103.61.224.178:9876/member/getET");
      const fetchedData = response.data.data || [];
      setData(fetchedData);
      setFilteredData(fetchedData); // Set initially
    } catch (error) {
      message.error("Failed to fetch expenses.");
    }
  };

  const handleExportExcel = () => {
    // Calculate the total of Amount
    const totalAmount = filteredData.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0; // Convert to number, default to 0 if not a number
      return sum + amount;
    }, 0);
  
    // Prepare data for export
    const excelData = filteredData.map((item, index) => ({
      "S.No": index + 1,
      "Expense Type": expenseOptions.find((exp) => exp.value === item.expensetype)?.label || "Unknown",
      Date: item.date,
      Remarks: item.remarks || "N/A",
      Amount: item.amount,
    }));
  
    // Add a row for the total
    const totalRow = {
      "S.No": "",
      "Expense Type": "",
      Date: "",
      Remarks: "Total",
      Amount: totalAmount.toFixed(2), // Format to 2 decimal places
    };
  
    // Combine data with total row
    const worksheetData = [...excelData, totalRow];
  
    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(worksheetData);
  
    // Optional: Style the total row (e.g., make it bold)
    const range = XLSX.utils.decode_range(ws["!ref"]);
    const totalRowIndex = range.e.r; // Last row (total row)
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });
      if (!ws[cellAddress]) continue;
      ws[cellAddress].s = {
        font: { bold: true },
        alignment: { horizontal: "right" },
      };
    }
  
    // Create workbook and append worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Expenses");
  
    // Export to file
    XLSX.writeFile(wb, "Expenses.xlsx");
  };
  
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Expense Report", 14, 10);
    
    const tableColumn = ["S.No", "Expense Type", "Amount", "Remarks", "Date"];
    const tableRows = filteredData.map((item, index) => [
      index + 1,
      expenseOptions.find((exp) => exp.value === item.expensetype)?.label || "Unknown",
      item.amount,
      item.remarks || "N/A",
      item.date,
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    doc.save("Expenses.pdf");
  };

  const fetchExpenseTypes = async () => {
    try {
      const response = await axios.get("http://103.61.224.178:9876/member/getexpense");
      setExpenseOptions(response.data.data.map((exp) => ({ value: exp._id, label: exp.expensetype })));
    } catch (error) {
      message.error("Failed to fetch expense types.");
    }
  };

  // Filter logic applied
  useEffect(() => {
    let filtered = data;

    if (selectedFilterExpense) {
      filtered = filtered.filter((item) => item.expensetype === selectedFilterExpense);
    }

    if (searchText.trim() !== "") {
      filtered = filtered.filter((item) =>
        (expenseOptions.find((exp) => exp.value === item.expensetype)?.label || "Unknown")
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        item.amount.toString().includes(searchText) ||
        (item.remarks && item.remarks.toLowerCase().includes(searchText.toLowerCase()))
      );
    }

    setFilteredData(filtered);
  }, [searchText, selectedFilterExpense, data]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`http://103.61.224.178:9876/member/deleteET/${id}`);
      message.success("Expense deleted successfully");
      fetchData();
    } catch (error) {
      message.error("Failed to delete expense.");
    }
  };

  const handleModalOpen = () => {
    setEditMode(false);
    setCurrentRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleFormSubmit = async (values) => {
    try {
      values.date = values.date.format("YYYY-MM-DD");
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === "uploadphoto" && values.uploadphoto?.length > 0) {
          formData.append("uploadphoto", values.uploadphoto[0].originFileObj);
        } else {
          formData.append(key, values[key]);
        }
      });

      if (editMode) {
        await axios.put(`http://103.61.224.178:9876/member/upET/${currentRecord._id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        message.success("Expense updated successfully");
      } else {
        await axios.post("http://103.61.224.178:9876/member/createET", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        message.success("Expense added successfully");
      }

      fetchData();
      setIsModalVisible(false);
    } catch (error) {
      message.error("Failed to save expense.");
    }
  };

  const columns = [
    { title: "S.No", render: (_, __, index) => index + 1 },
    { title: "Expense Type", dataIndex: "expensetype", render: (id) => expenseOptions.find((exp) => exp.value === id)?.label || "Unknown" },
    { title: "Amount", dataIndex: "amount" },
    { title: "Remarks", dataIndex: "remarks" },
    { title: "Date", dataIndex: "date" },
    {
      title: "Receipt Photo",
      dataIndex: "uploadphoto",
      render: (uploadphoto) => {
        if (!uploadphoto) {
          return <img src="https://via.placeholder.com/50x50?text=No+Image" alt="No Receipt" style={{ width: 50, height: 50, borderRadius: 4 }} />;
        }
        const imageUrl = `http://103.61.224.178:9876/${uploadphoto}`;
        return <img src={imageUrl} alt="Receipt" style={{ width: 50, height: 50, borderRadius: 4, objectFit: "cover" }} onError={(e) => { e.target.src = "https://via.placeholder.com/50x50?text=Error"; }} />;
      },
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditMode(true);
              setCurrentRecord(record);
              // Prepare fileList for edit mode if uploadphoto exists
              const fileList = record.uploadphoto ? [{ uid: -1, name: 'receipt.jpg', status: 'done', url: `http://103.61.224.178:9876/${record.uploadphoto}` }] : [];
              form.setFieldsValue({ 
                ...record, 
                date: moment(record.date),
                uploadphoto: fileList 
              });
              setIsModalVisible(true);
            }}
          />
          <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
        <Col span={4}>
          <Select
            placeholder="Filter by Expense Type"
            allowClear
            style={{ width: "100%" }}
            onChange={setSelectedFilterExpense}
            options={expenseOptions}
          />
        </Col>
        <Col span={6}>
          <Input
            placeholder="Search Expense"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
 
        <Col>
          <Button icon={<FileExcelOutlined />} style={{ marginLeft: 8 }} onClick={handleExportExcel}>
            Export Excel
          </Button>
          <Button icon={<FilePdfOutlined />} style={{ marginLeft: 8 }} onClick={handleExportPDF}>
            Export PDF
          </Button>
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Button icon={<PlusOutlined />} type="primary" onClick={handleModalOpen}>
            Add Expense
          </Button>
        </Col>
      </Row>
      <Table columns={columns} dataSource={filteredData} rowKey="_id" />

      <Modal
        title={editMode ? "Edit Expense" : "Add Expense"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Form.Item name="expensetype" label="Expense Type" rules={[{ required: true }]}>
            <Select options={expenseOptions} />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>
          <Form.Item 
            label="Receipt" 
            name="uploadphoto" 
            valuePropName="fileList" 
            getValueFromEvent={(e) => Array.isArray(e) ? e : e && e.fileList}
          >
            <Upload beforeUpload={() => false} listType="picture" maxCount={1}>
              <Button icon={<UploadOutlined />}>Upload Receipt</Button>
            </Upload>
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="date" label="Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ExpenseForm;